import { TextArea } from '@sunwebgroup/uds-ui/fields/textarea-field'
import * as React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useIntl } from '@/intl'
import { getErrorMessage } from '@/scopes/common/components/FormElements/getErrorMessage'
import { validatePattern } from '@/scopes/common/components/FormElements/TextInput'
import { useStatus } from '@/scopes/common/components/FormElements/useStatus'

export interface TextAreaInputProps {
  name: string
  label?: string
  placeholder?: string
  pattern?: string
  patternErrorMessage?: string
  required?: boolean
  requiredErrorMessage?: string
  formFieldClass?: string
  helpText?: string
  fieldLengthValidation?: number
}

export function TextAreaInput({
  name,
  label,
  placeholder,
  pattern,
  patternErrorMessage,
  required,
  requiredErrorMessage,
  formFieldClass,
  helpText,
  fieldLengthValidation = 1,
}: TextAreaInputProps) {
  const intl = useIntl()
  const { control, formState } = useFormContext()
  const validations = React.useMemo(
    () => ({
      ...(pattern ? { matchPattern: (value: string) => validatePattern(value, pattern) } : {}),
    }),
    [pattern],
  )

  const {
    field: { value, onChange, onBlur, ref },
  } = useController({
    name,
    control,
    rules: {
      required,
      validate: validations,
    },
  })

  const { errors } = formState
  const shouldMarkValidStatus = React.useCallback(
    (fieldValue: string) => fieldValue?.trim().length >= fieldLengthValidation,
    [fieldLengthValidation],
  )
  const status = useStatus({
    name,
    required,
    validations,
    shouldMarkValidStatus,
  })
  const errorMessage = getErrorMessage(
    name,
    errors,
    { patternErrorMessage, requiredErrorMessage },
  )

  function handleBlur(event: React.FocusEvent<HTMLTextAreaElement>) {
    onChange(typeof event.target.value === 'string' ? event.target.value.trim() : event.target.value)
    onBlur()
  }

  return (
    <TextArea
      ref={ref}
      label={label}
      placeholder={placeholder}
      feedbackText={errorMessage}
      status={status}
      formFieldClass={formFieldClass}
      optionalLabel={!required ? intl.formatMessage({ id: 'common_form_field_optional_label' }) : undefined}
      helpText={helpText}
      name={name}
      value={value ?? ''}
      onChange={event => onChange(event.target.value)}
      onBlur={handleBlur}
    />
  )
}
